import {queryOptions} from '@tanstack/react-query'
import {notFound} from '@tanstack/react-router'
import imageUrlBuilder from '@sanity/image-url'
import {createServerFn} from '@tanstack/react-start'
import {z} from 'zod'
import fetchDesignCollectionDetails from '~/client/spreadshop/old/fetchDesignCollectionDetails'
import fetchPerfectFitCollection from '~/client/sanity/fetchPerfectFitCollection'
import {applyServerFnCache} from '~/client/fn/utils'
import client from '~/client/sanity/Client'
import {Locale} from '~/gql/graphql'

const IMAGE_BUILDER = imageUrlBuilder(client)
const CACHE_TIME_SEC = 60 * 60 * 3 // 3h

const perfectFitDesignParams = z
    .object({
        locale: z.nativeEnum(Locale).optional(),
    })
    .optional()

export const getPerfectFitCollection = createServerFn({
    method: 'GET',
})
    .inputValidator((data: unknown) => {
        const result = perfectFitDesignParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }

        return result.data
    })
    .handler(async ({data}) => {
        const {locale} = data ?? {}
        const sanityCollectionDetails = await fetchPerfectFitCollection(locale)

        const collectionId = sanityCollectionDetails?.collection.id
        if (!collectionId) {
            return undefined
        }

        const sprdCollectionDetails = await fetchDesignCollectionDetails(collectionId, locale)

        if (!sprdCollectionDetails?.designs.length) {
            return undefined
        }

        applyServerFnCache(CACHE_TIME_SEC)

        return {
            ...sprdCollectionDetails,
            name: sanityCollectionDetails?.overwrites?.title ?? sprdCollectionDetails.name,
            description:
                sanityCollectionDetails?.overwrites?.description ??
                sprdCollectionDetails.description,
            seo: {
                ...sanityCollectionDetails?.seo,
                image: sanityCollectionDetails?.seo?.image
                    ? IMAGE_BUILDER.image(sanityCollectionDetails.seo.image)
                          .width(500)
                          .height(250)
                          .url()
                    : undefined,
            },
        }
    })

export const perfectFitCollectionDetailsQueryOptions = (locale: Locale = Locale.EnEu) =>
    queryOptions({
        queryKey: [locale, 'collections', 'perfect-fit'],
        queryFn: () => getPerfectFitCollection({data: {locale}}),
        gcTime: 1000 * CACHE_TIME_SEC, // 3h
        staleTime: 1000 * CACHE_TIME_SEC, // 3h
    })
